import { useRef, useState } from "react";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Save, Loader2, Crop as CropIcon } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { makeProcessedBlob } from "@/lib/imageProcess";

// Reusable crop-before-upload modal (Radix Dialog based; works nested inside other dialogs).
// onApply(blob) receives the cropped, downscaled, 250dpi JPEG blob.
export default function CropModal({ src, onCancel, onApply, busy }) {
  const imgRef = useRef(null);
  const [crop, setCrop] = useState(null);
  const [completedCrop, setCompletedCrop] = useState(null);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(
      centerCrop(makeAspectCrop({ unit: "%", width: 100 }, width / height, width, height), width, height)
    );
  };

  const apply = async () => {
    if (!imgRef.current) return;
    const c =
      completedCrop && completedCrop.width
        ? completedCrop
        : { x: 0, y: 0, width: imgRef.current.width, height: imgRef.current.height };
    const blob = await makeProcessedBlob(imgRef.current, c);
    onApply(blob);
  };

  return (
    <Dialog open={!!src} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="crop-modal">
        <DialogHeader className="px-4 py-3 border-b border-stone-200">
          <DialogTitle className="text-sm font-medium text-stone-900 flex items-center gap-2">
            <CropIcon className="h-4 w-4 text-emerald-700" />画像を切り取り
          </DialogTitle>
        </DialogHeader>
        <div className="bg-stone-900/90 flex items-center justify-center p-3 max-h-[62vh] overflow-auto">
          {src && (
            <ReactCrop crop={crop} onChange={(_, pc) => setCrop(pc)} onComplete={(c) => setCompletedCrop(c)}>
              <img ref={imgRef} src={src} alt="crop" onLoad={onImageLoad} style={{ maxHeight: "58vh", maxWidth: "100%" }} />
            </ReactCrop>
          )}
        </div>
        <div className="border-t border-stone-200 px-4 py-3 flex items-center justify-between gap-2">
          <p className="text-xs text-stone-500">ドラッグで範囲を調整。保存時に250dpiへ圧縮します。</p>
          <div className="flex items-center gap-2">
            <button onClick={onCancel} data-testid="crop-cancel" className="rounded-md px-3 py-2 text-sm font-medium text-stone-600 hover:bg-stone-100">
              キャンセル
            </button>
            <button onClick={apply} disabled={busy} data-testid="crop-apply" className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              切り取って使用
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
